export const name = "SEARCH";
export const summary = `Checks to see whether a table or other search data contains a set of search terms.`;
export const reference = `
## SEARCH

Checks to see whether a table or other search data contains a set of search terms.

\`\`\`
SEARCH(
  data_to_search, search_query
  [, json_scope => { 'JSON_VALUES' | 'JSON_KEYS' | 'JSON_KEYS_AND_VALUES' }]
  [, analyzer => { 'LOG_ANALYZER' | 'NO_OP_ANALYZER' | 'PATTERN_ANALYZER'}]
  [, analyzer_options => analyzer_options_values]
)
\`\`\`

### Description

The \`SEARCH\` function checks to see whether a BigQuery table or other search data contains a set of search terms (tokens). It returns \`TRUE\` if all search terms appear in the data, based on the rules for search_queryand text analysis described in the text analyzer. Otherwise, this function returns \`FALSE\`.


### Definitions



- \`data_to_search\`: The data to search over. The value can be:



- \`search_query\`: A \`STRING\` literal, or a \`STRING\` constant expression that represents the terms of the search query. If \`search_query\` is \`NULL\`, an error is returned. If \`search_query\` produces no search tokens, and the text analyzer is \`LOG_ANALYZER\` or \`PATTERN_ANALYZER\`, an error is produced.
- \`json_scope\`: A named argument with a \`STRING\` value. Takes one of the following values to indicate the scope of JSON data to be searched. It has no effect if \`data_to_search\` isn't a JSON value or doesn't contain a JSON field.

- \`analyzer\`: A named argument with a \`STRING\` value. Takes one of the following values to indicate the text analyzer to use:

- \`analyzer_options\`: A named argument with a JSON-formatted \`STRING\` value. Takes a list of text analysis rules. For more information, see Text analyzer options.


### Details

The \`SEARCH\` function is designed to work with search indexes to optimize point lookups. Although the \`SEARCH\` function works for tables that aren't indexed, its performance will be greatly improved with a search index. If both the analyzer and analyzer options match the one used to create the index, the search index will be used.






### Rules for search_query

A search query is initially broken down into terms (subqueries) using the white spaces in the search query. Each term is then further broken down into zero or more searchable tokens based on the text analyzer. This section contains the rules for how different types of terms are analyzed and evaluated.

Rules for backticks in \`search_query\`:

- If the \`LOG_ANALYZER\` text analyzer is used, text enclosed in backticks forces an exact match.

- Search terms enclosed in backticks must match exactly in \`data_to_search\`, subject to the following conditions:

- Search terms enclosed in backticks must match case exactly, regardless of any normalization settings in \`analyzer_options\`.

- The backtick itself can be escaped using a backslash, as in \`\\\`foobar\\\`\`.

- The following are reserved words and must be enclosed in backticks:

Rules for reserved characters in \`search_query\`:

- Text not enclosed in backticks requires the following reserved characters to be escaped by a double backslash\`\\\\\`:

Rules for phrases in \`search_query\`:

- A phrase is a type of term. If text is enclosed in double quotes and the\`analyzer\` is \`LOG_ANALYZER\`, \`PATTERN_ANALYZER\`, or not set (\`LOG_ANALYZER\` by default), the term represents a phrase.
- When a phrase is analyzed, a subset of tokens is created for that phrase. For example, from the phrase \`"foo baz.bar"\`, the analyzer called\`LOG_ANALYZER\` generates the phrase-specific tokens \`foo\`, \`baz\`, and \`bar\`.
- The order of terms in a phrase matters. A match is only returned if the tokens that were produced for the phrase are next to each other and in the same order as the tokens for \`data_to_search\`.

- A single quote inside of the phrase is analyzed as a special character.

- An escaped double quote (double quote after a backslash) is analyzed as a double quote character.




### How search_query is broken into searchable tokens

The following table shows how \`search_query\` is broken into searchable tokens by the \`LOG_ANALYZER\` text analyzer. All entries are strings.

| search\\_query | searchable tokens |
| --- | --- |
| 127.0.0.1 | 127   
0   
1   
127.0.0.1   
. 127.0.0   
127.0   
0.0   
0.0.1   
0.1 |
| foobar@example.com | foobar   
example   
com   
foobar@example   
example.com   
foobar@example.com |
| The fox. | the   
fox   
The   
The fox   
The fox.   
fox   
fox. |

The following table shows how \`search_query\` is broken into searchable tokens by the \`LOG_ANALYZER\` text analyzer. All entries are strings.

| search\\_query | searchable tokens |
| --- | --- |
| 127.0.0.1 | 127   
0   
1   
 |
| \`127.0.0.1\` | 127.0.0.1 |
| foobar@example.com | foobar   
example   
com |
| \`foobar@example.com\` | foobar@example.com |




### Rules for data_to_search

General rules for \`data_to_search\`:

- \`data_to_search\` must contain all tokens produced for\`search_query\` for the function to return \`TRUE\`.
- To perform a cross-field search, \`data_to_search\` must be a \`STRUCT\`,\`ARRAY\`, or \`JSON\` data type.
- Each \`STRING\` field in a compound data type is individually searched for terms.
- If at least one field in \`data_to_search\` includes all search terms produced by \`search_query\`, \`SEARCH\` returns \`TRUE\`. Otherwise it has the following behavior:


### Return type

\`BOOL\`


### Examples

The following queries show how tokens in \`search_query\` are analyzed by a \`SEARCH\` function call using the default analyzer, \`LOG_ANALYZER\`:



\`\`\`
SELECT
  -- ERROR: \`search_query\` is NULL.
  SEARCH('foobarexample', NULL) AS a,

  -- ERROR: \`search_query\` contains no tokens.
  SEARCH('foobarexample', '') AS b,
\`\`\`



\`\`\`
SELECT
  -- TRUE: '-' and ' ' are delimiters.
  SEARCH('foobar-example', 'foobar example') AS a,

  -- TRUE: The search query is a constant expression evaluated to 'foobar'.
  SEARCH('foobar-example', CONCAT('foo', 'bar')) AS b,

  -- FALSE: The search_query isn't split.
  SEARCH('foobar-example', 'foobarexample') AS c,

  -- TRUE: The double backslash escapes the ampersand which is a delimiter.
  SEARCH('foobar-example', 'foobar\\\\&example') AS d,

  -- TRUE: The single backslash escapes the ampersand in a raw string.
  SEARCH('foobar-example', R'foobar\\&example')AS e,

  -- FALSE: The backticks indicate that there must be an exact match for
  -- foobar&example.
  SEARCH('foobar-example', '\`foobar&example\`') AS f,

  -- TRUE: An exact match is found.
  SEARCH('foobar&example', '\`foobar&example\`') AS g

/*-------+-------+-------+-------+-------+-------+-------*
 | a | b | c | d | e | f | g |
 +-------+-------+-------+-------+-------+-------+-------+
 | true | true | false | true | true | false | true |
 *-------+-------+-------+-------+-------+-------+-------*/
\`\`\`



\`\`\`
SELECT
  -- TRUE: The order of terms doesn't matter.
  SEARCH('foobar-example', 'example foobar') AS a,

  -- TRUE: Tokens are made lower-case.
  SEARCH('foobar-example', 'Foobar Example') AS b,

  -- TRUE: An exact match is found.
  SEARCH('foobar-example', '\`foobar-example\`') AS c,

  -- FALSE: Backticks preserve capitalization.
  SEARCH('foobar-example', '\`Foobar\`') AS d,

  -- FALSE: Backticks don't have special meaning for search_data and are
  -- not delimiters in the default LOG_ANALYZER.
  SEARCH('\`foobar-example\`', '\`foobar-example\`') AS e,

  -- TRUE: An exact match is found after the delimiter in search_data.
  SEARCH('foobar@example.com', '\`example.com\`') AS f,

  -- TRUE: An exact match is found between the space delimiters.
  SEARCH('a foobar-example b', '\`foobar-example\`') AS g;

/*-------+-------+-------+-------+-------+-------+-------*
 | a | b | c | d | e | f | g |
 +-------+-------+-------+-------+-------+-------+-------+
 | true | true | true | false | false | true | true |
 *-------+-------+-------+-------+-------+-------+-------*/
\`\`\`



\`\`\`
SELECT
  -- FALSE: No single array entry matches all search terms.
  SEARCH(['foobar', 'example'], 'foobar example') AS a,

  -- FALSE: The search query is equivalent to foobar\\\\=.
  SEARCH('foobar=', '\`foobar\\\\=\`') AS b,

  -- FALSE: This is equivalent to the previous example.
  SEARCH('foobar=', R'\`\\foobar=\`') AS c,

  -- TRUE: The equals sign is a delimiter in the data and query.
  SEARCH('foobar=', 'foobar\\\\=') AS d,

  -- TRUE: This is equivalent to the previous example.
  SEARCH('foobar=', R'foobar\\=') AS e,

  -- TRUE: An exact match is found.
  SEARCH('foobar.example', '\`foobar\`') AS f,

  -- FALSE: \`foobar.\\\` isn't analyzed because of backticks; it isn't
  -- followed by a delimiter in search_data 'foobar.example'.
  SEARCH('foobar.example', '\`foobar.\\\`') AS g,

  -- TRUE: \`foobar.\` isn't analyzed because of backticks; it is
  -- followed by the delimiter '.' in search_data 'foobar..example'.
  SEARCH('foobar..example', '\`foobar.\`') AS h;

/*-------+-------+-------+-------+-------+-------+-------+-------*
 | a | b | c | d | e | f | g | h |
 +-------+-------+-------+-------+-------+-------+-------+-------+
 | false | false | false | true | true | true | false | true |
 *-------+-------+-------+-------+-------+-------+-------+-------*/
\`\`\`

The following queries show how phrases in \`search_query\` are analyzed by a \`SEARCH\` function call:



\`\`\`
SELECT
  -- TRUE: The phrase \`foo bar\` is in \`foo bar baz\`.
  -- The tokens in \`data_to_search\` are \`foo\`, \`bar\`, and \`baz\`.
  -- The searchable tokens in \`query_string\` are \`foo\` and \`bar\`
  -- and because they appear in that exact order in \`data_to_search\`,
  -- the function returns TRUE.
  SEARCH(R'foo bar baz', R'"foo bar"') AS a,

  -- TRUE: Case is ignored.
  -- The tokens in \`data_to_search\` are \`foo\`, \`bar\`, and \`baz\`.
  -- The searchable tokens in \`query_string\` are \`foo\` and \`bar\`
  -- and because they appear in that exact order in \`data_to_search\`,
  -- the function return TRUE.
  SEARCH(R'Foo bar baz', R'"foo Bar"') AS b,

  -- TRUE: Both \`-\` and \`&\` are delimiters used during tokenization.
  -- The tokens in \`data_to_search\` are \`foo\`, \`bar\`, and \`baz\`.
  -- The searchable tokens in \`query_string\` are \`foo\` and \`bar\`
  -- and because they appear in that exact order in \`data_to_search\`,
  -- the function returns TRUE.
  SEARCH(R'foo-bar baz', R'"foo&bar"') AS c,

  -- FALSE: Backticks in a phrase are treated as normal characters.
  -- The tokens in \`data_to_search\` are \`foo\`, \`bar\`, and \`baz\`.
  -- The searchable tokens in \`query_string\` are:
  -- \`foo
  -- bar\`
  -- Because these searchable tokens don't appear in \`data_to_search\`,
  -- the function returns FALSE.
  SEARCH(R'foo bar baz', R'"\`foo bar\`"') AS d,

  -- FALSE: \`foo bar\` isn't in \`foo else bar\`.
  -- The tokens in \`data_to_search\` are \`foo\`, \`else\`, and \`bar\`.
  -- The searchable tokens in \`query_string\` are \`foo\` and \`bar\`.
  -- Even though they appear in \`data_to_search\`, but because they
  -- don't appear in that exact order (\`foo\` before \`bar\`),
  -- the function returns FALSE.
  SEARCH(R'foo else bar', R'"foo bar"') AS e,

  -- FALSE: \`foo baz\` isn't in \`foo bar baz\`.
  -- The \`search_query\` produces two terms. The first term is \`bar\`, which
  -- matches with the similar token in \`data_to_search\`. However, the second
  -- term is the phrase "foo&baz" with two tokens, \`foo\` and \`baz\`. Because
  -- \`foo\` and \`baz\` don't appear next to each other in \`data_to_search\`
  -- (\`bar\` is in between), the function returns FALSE.
  SEARCH(R'foo-bar-baz', R'bar "foo&baz"') AS f;

/*-------+-------+-------+-------+-------+-------*
 | a | b | c | d | e | f |
 +-------+-------+-------+-------+-------+-------+
 | true | true | false | false | false | false |
 *-------+-------+-------+-------+-------+-------*/
\`\`\`



\`\`\`
SELECT
  -- FALSE: Only double quotes need to be escaped in a phrase.
  -- The tokens in \`data_to_search\` are \`foo\`, \`bar\`, and \`baz\`.
  -- The searchable tokens in \`query_string\` are \`foo\\\` and \`bar\` and they
  -- must appear in that exact order in \`data_to_search\`, but don't.
  SEARCH(
    R'foo bar baz',
    R'"foo\\ bar"',
    analyzer_options=>'{"delimiters": [" "]}') AS a,

  -- TRUE: \`foo bar\` is in \`foo bar baz\` after tokenization with the given
  -- delimiters.
  -- The tokens in \`data_to_search\` are \`foo\`, \`bar\`, and \`baz\`.
  -- The searchable tokens in \`query_string\` are \`foo\` and \`bar\` and they
  -- must appear in that exact order in \`data_to_search\`.
  SEARCH(
    R'foo bar baz',
    R'"foo? bar"',
    analyzer_options=>'{"delimiters": [" ", "?"]}') AS b,

  -- TRUE: \`read book\` is in \`read book now\` after \`the\` is ignored.
  -- The tokens in \`data_to_search\` are \`read\`, \`book\`, and \`now\`.
  -- The searchable tokens in \`query_string\` are \`read\` and \`book\` and they
  -- must appear in that exact order in \`data_to_search\`.
  SEARCH(
    'read the book now',
    R'"read the book"',
    analyzer_options => '{ "token_filters": [{"stop_words": ["the"]}] }') AS c,

  -- FALSE: \`c d\` isn't in \`a\`, \`b\`, \`cd\`, \`e\` or \`f\` after tokenization with
  -- the given pattern.
  -- The tokens in \`data_to_search\` are \`a\`, \`b\`, \`cd\`, \`e\` and \`f\`.
  -- The searchable tokens in \`query_string\` are \`c\` and \`d\` and they
  -- must appear in that exact order in \`data_to_search\`. \`data_to_search\`
  -- contains a \`cd\` token, but not a \`c\` or \`d\` token.
  SEARCH(
    R'abcdef',
    R'"c d"',
    analyzer=>'PATTERN_ANALYZER',
    analyzer_options=>'{"patterns": ["(?:cd)|[a-z]"]}') AS d,

  -- TRUE: \`ant apple\` is in \`ant apple avocado\` after tokenization with
  -- the given pattern.
  -- The tokens in \`data_to_search\` are \`ant\`, \`apple\`, and \`avocado\`.
  -- The searchable tokens in \`query_string\` are \`ant\` and \`apple\` and they
  -- must appear in that exact order in \`data_to_search\`.
  SEARCH(
    R'ant orange apple avocado',
    R'"ant apple"',
    analyzer=>'PATTERN_ANALYZER',
    analyzer_options=>'{"patterns": ["a[a-z]"]}') AS e;

/*-------+-------+-------+-------+-------*
 | a | b | c | d | e |
 +-------+-------+-------+-------+-------+
 | false | true | true | false | true |
 *-------+-------+-------+-------+-------*/
\`\`\`

The following query shows examples of calls to the \`SEARCH\` function using the\`NO_OP_ANALYZER\` text analyzer and reasons for various return values:



\`\`\`
SELECT
  -- TRUE: exact match
  SEARCH('foobar', 'foobar', analyzer=>'NO_OP_ANALYZER') AS a,

  -- FALSE: Backticks aren't special characters for \`NO_OP_ANALYZER\`.
  SEARCH('foobar', '\\\`foobar\\\`', analyzer=>'NO_OP_ANALYZER') AS b,

  -- FALSE: The capitalization doesn't match.
  SEARCH('foobar', 'Foobar', analyzer=>'NO_OP_ANALYZER') AS c,

  -- FALSE: There are no delimiters for \`NO_OP_ANALYZER\`.
  SEARCH('foobar example', 'foobar', analyzer=>'NO_OP_ANALYZER') AS d,

  -- TRUE: An exact match is found.
  SEARCH('', '', analyzer=>'NO_OP_ANALYZER') AS e,

  -- FALSE: 'foo bar' and "foo bar" aren't considered an exact match.
  SEARCH( R'foo bar baz', R'"foo bar"', analyzer=>'NO_OP_ANALYZER') AS f,

  -- TRUE: "foo bar" and "foo Bar" are considered an exact match because the
  -- analysis is case-insensitive.
  SEARCH( R'"foo bar"', R'"foo Bar"', analyzer=>'NO_OP_ANALYZER') AS g;

/*-------+-------+-------+-------+-------+-------+-------*
 | a | b | c | d | e | f | g |
 +-------+-------+-------+-------+-------+-------+-------+
 | true | false | false | false | true | false | true |
 *-------+-------+-------+-------+-------+-------+-------*/
\`\`\`

Consider the following table called \`meals\` with columns \`breakfast\`, \`lunch\`, and \`dinner\`:



\`\`\`
/*-------------------+-------------------------+------------------*
 | breakfast | lunch | dinner |
 +-------------------+-------------------------+------------------+
 | Potato pancakes | Toasted cheese sandwich | Beef soup |
 | Avocado toast | Tomato soup | Chicken soup |
 *-------------------+-------------------------+------------------*/
\`\`\`

The following query shows how to search single columns, multiple columns, and whole tables, using the default \`LOG_ANALYZER\` text analyzer with the default analyzer options:



\`\`\`
WITH
  meals AS (
    SELECT
      'Potato pancakes' AS breakfast,
      'Toasted cheese sandwich' AS lunch,
      'Beef soup' AS dinner
    UNION ALL
    SELECT
      'Avocado toast' AS breakfast,
      'Tomato soup' AS lunch,
      'Chicken soup' AS dinner
  )
SELECT
  SEARCH(lunch, 'soup') AS lunch_soup,
  SEARCH((breakfast, dinner), 'soup') AS breakfast_or_dinner_soup,
  SEARCH(meals, 'soup') AS anytime_soup
FROM meals;

/*------------+--------------------------+--------------*
 | lunch_soup | breakfast_or_dinner_soup | anytime_soup |
 +------------+--------------------------+--------------+
 | false | true | true |
 | true | true | true |
 *------------+--------------------------+--------------*/
\`\`\`

The following query shows additional ways to search, using the default \`LOG_ANALYZER\` text analyzer with default analyzer options:



\`\`\`
WITH data AS ( SELECT 'Please use foobar@example.com as your email.' AS email )
SELECT
  SEARCH(email, 'exam') AS a,
  SEARCH(email, 'foobar') AS b,
  SEARCH(email, 'example.com') AS c,
  SEARCH(email, R'"please use"') AS d,
  SEARCH(email, R'"as email"') AS e
FROM data;

/*-------+-------+-------+-------+-------*
 | a | b | c | d | e |
 +-------+-------+-------+-------+-------+
 | false | true | true | true | false |
 *-------+-------+-------+-------+-------*/
\`\`\`

The following query shows additional ways to search, using the default \`LOG_ANALYZER\` text analyzer with custom analyzer options. Terms are only split when a space or \`@\` symbol is encountered.



\`\`\`
WITH data AS ( SELECT 'Please use foobar@example.com as your email.' AS email )
SELECT
  SEARCH(email, 'foobar', analyzer_options=>'{"delimiters": [" ", "@"]}') AS a,
  SEARCH(email, 'example', analyzer_options=>'{"delimiters": [" ", "@"]}') AS b,
  SEARCH(email, 'example.com', analyzer_options=>'{"delimiters": [" ", "@"]}') AS c,
  SEARCH(email, 'foobar@example.com', analyzer_options=>'{"delimiters": [" ", "@"]}') AS d,
  SEARCH(email, R'use "foobar example.com" "as your"', analyzer_options=>'{"delimiters": [" ", "@"]}') AS e
FROM data;

/*-------+-------+-------+-------+-------*
 | a | b | c | d | e |
 +-------+-------+-------+-------+-------+
 | true | false | true | true | true |
 *-------+-------+-------+-------+-------*/
\`\`\`

The following query shows how to search, using the \`NO_OP_ANALYZER\` text analyzer:



\`\`\`
WITH meals AS ( SELECT 'Tomato soup' AS lunch )
SELECT
  SEARCH(lunch, 'Tomato soup', analyzer=>'NO_OP_ANALYZER') AS a,
  SEARCH(lunch, 'soup', analyzer=>'NO_OP_ANALYZER') AS b,
  SEARCH(lunch, 'tomato soup', analyzer=>'NO_OP_ANALYZER') AS c,
  SEARCH(lunch, R'"Tomato soup"', analyzer=>'NO_OP_ANALYZER') AS d
FROM meals;

/*-------+-------+-------+-------*
 | a | b | c | d |
 +-------+-------+-------+-------+
 | true | false | false | false |
 *-------+-------+-------+-------*/
\`\`\`

The following query shows how to use the \`PATTERN_ANALYZER\`text analyzer with default analyzer options:



\`\`\`
WITH data AS ( SELECT 'Please use foobar@example.com as your email.' AS email )
SELECT
  SEARCH(email, 'exam', analyzer=>'PATTERN_ANALYZER') AS a,
  SEARCH(email, 'foobar', analyzer=>'PATTERN_ANALYZER') AS b,
  SEARCH(email, 'example.com', analyzer=>'PATTERN_ANALYZER') AS c,
  SEARCH(email, R'foobar "EXAMPLE.com as" email', analyzer=>'PATTERN_ANALYZER') AS d
FROM data;

/*-------+-------+-------+-------*
 | a | b | c | d |
 +-------+-------+-------+-------+
 | false | true | true | true |
 *-------+-------+-------+-------*/
\`\`\`

The following query shows additional ways to search, using the \`PATTERN_ANALYZER\` text analyzer with custom analyzer options:



\`\`\`
WITH data AS ( SELECT 'Please use foobar@EXAMPLE.com as your email.' AS email )
SELECT
  SEARCH(email, 'EXAMPLE', analyzer=>'PATTERN_ANALYZER', analyzer_options=>'{"patterns": ["[A-Z]*"]}') AS a,
  SEARCH(email, 'example', analyzer=>'PATTERN_ANALYZER', analyzer_options=>'{"patterns": ["[a-z]*"]}') AS b,
  SEARCH(email, 'example.com', analyzer=>'PATTERN_ANALYZER', analyzer_options=>'{"patterns": ["[a-z]*"]}') AS c,
  SEARCH(email, 'example.com', analyzer=>'PATTERN_ANALYZER', analyzer_options=>'{"patterns": ["[a-zA-Z.]*"]}') AS d
FROM data;

/*-------+-------+-------+-------+-------*
 | a | b | c | d | e |
 +-------+-------+-------+-------+-------+
 | true | false | false | true | false |
 *-------+-------+-------+-------+-------*/
\`\`\`

For additional examples that include analyzer options, see the Text analysis reference guide.

For helpful analyzer recipes that you can use to enhance analyzer-supported queries, see the Search with text analyzers user guide.


source: https://cloud.google.com/bigquery/docs/reference/standard-sql/search_functions
`;
